import { useCallback, useMemo } from "react";
import { useDealStore, useContactStore, useDealTypeStore, useProjectStore, useUserStore, useSyncStore } from "@/stores/StoreProvider";
import type { Deal, DealRequest } from "@/types/crm";
import { dealsApi, dealTypesApi } from "@/lib/api/crm";
import { toast } from "sonner";
import { toastUndo } from "@/components/ui/undo-toast";

export function useDeals() {
  const dealStore = useDealStore();
  const contactStore = useContactStore();
  const dealTypeStore = useDealTypeStore();
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const deals = dealStore.all;
  const data = useMemo(() => deals.map(d => ({
    ...d,
    dealTypeName: d.dealTypeId ? dealTypeStore.getById(d.dealTypeId)?.name ?? '' : '',
    contactName: d.contactId ? contactStore.getById(d.contactId)?.name : undefined,
    projectName: d.projectId ? projectStore.getById(d.projectId)?.name : undefined,
    ownerName: d.ownerId ? userStore.getById(d.ownerId)?.displayName : undefined,
  })) as Deal[], [deals]);
  return { data, isLoading: !syncStore.bootstrapComplete };
}

export function useContactDeals(contactId: string) {
  const dealStore = useDealStore();
  const dealTypeStore = useDealTypeStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const rawDeals = contactId ? dealStore.getByContactId(contactId) : [];
  const data = useMemo(() => rawDeals.map(d => ({
    ...d,
    dealTypeName: d.dealTypeId ? dealTypeStore.getById(d.dealTypeId)?.name ?? '' : '',
    ownerName: d.ownerId ? userStore.getById(d.ownerId)?.displayName : undefined,
  })) as Deal[], [rawDeals]);
  return { data, isLoading: !syncStore.bootstrapComplete };
}

export function useProjectDeals(projectId: string) {
  const dealStore = useDealStore();
  const dealTypeStore = useDealTypeStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const rawDeals = projectId ? dealStore.getByProjectId(projectId) : [];
  const data = useMemo(() => rawDeals.map(d => ({
    ...d,
    dealTypeName: d.dealTypeId ? dealTypeStore.getById(d.dealTypeId)?.name ?? '' : '',
    ownerName: d.ownerId ? userStore.getById(d.ownerId)?.displayName : undefined,
  })) as Deal[], [rawDeals]);
  return { data, isLoading: !syncStore.bootstrapComplete };
}

export function useDeal(id: string) {
  const dealStore = useDealStore();
  const contactStore = useContactStore();
  const dealTypeStore = useDealTypeStore();
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const raw = id ? dealStore.getById(id) : undefined;
  const data = useMemo(() => raw ? {
    ...raw,
    dealTypeName: raw.dealTypeId ? dealTypeStore.getById(raw.dealTypeId)?.name ?? '' : '',
    contactName: raw.contactId ? contactStore.getById(raw.contactId)?.name : undefined,
    projectName: raw.projectId ? projectStore.getById(raw.projectId)?.name : undefined,
    ownerName: raw.ownerId ? userStore.getById(raw.ownerId)?.displayName : undefined,
  } as Deal : undefined, [raw]);
  return { data, isLoading: !data && !syncStore.bootstrapComplete };
}

export function useCreateDeal() {
  const dealStore = useDealStore();
  const dealTypeStore = useDealTypeStore();
  const create = async (data: DealRequest) => {
    const needsApiPath = !data.dealTypeId || (data.newFields && data.newFields.length > 0);

    if (needsApiPath) {
      // Inline type creation or schema extension: must go through REST API
      // (sync push endpoint deserializes raw Deal entities and can't handle newDealTypeName/newFields)
      const deal = await dealsApi.create(data);
      // Inject the real deal into the store without queuing a sync mutation
      dealStore.applyChanges([{ id: deal.id, data: deal as unknown as Record<string, unknown>, isDeleted: false }]);
      // Also fetch and inject the DealType so the name renders immediately
      try {
        const dt = await dealTypesApi.getById(deal.dealTypeId);
        dealTypeStore.applyChanges([{ id: dt.id, data: dt as unknown as Record<string, unknown>, isDeleted: false }]);
      } catch {
        // Non-fatal: delta sync will pick it up
      }
    } else {
      // Normal path: optimistic store update → sync queue
      const { newDealTypeName: _, newFields: __, ...storeData } = data;
      dealStore.create({ ...storeData, dealTypeId: storeData.dealTypeId ?? undefined });
    }
    toast.success("Deal created");
  };
  return { mutateAsync: create, mutate: (data: DealRequest) => { create(data); }, isPending: false };
}

export function useUpdateDeal() {
  const dealStore = useDealStore();
  const update = async ({ id, data }: { id: string; data: DealRequest }) => {
    try {
      const { newDealTypeName: _, newFields: __, ...storeData } = data;
      dealStore.update(id, { ...storeData, dealTypeId: storeData.dealTypeId ?? undefined });
      toast.success("Deal updated");
    } catch (error) {
      toast.error("Failed to update deal");
      throw error;
    }
  };
  return { mutateAsync: update, mutate: (args: { id: string; data: DealRequest }) => { update(args); }, isPending: false };
}

export function useDeleteDeal() {
  const dealStore = useDealStore();
  const syncStore = useSyncStore();
  const mutateAsync = useCallback(
    async ({ id, name }: { id: string; name?: string }) => {
      dealStore.delete(id);
      toastUndo(
        name ? `"${name}" deleted` : "Deal deleted",
        () => {
          syncStore.cancelPendingMutation('deal', 'delete', id);
          dealStore.restore(id);
        },
      );
    },
    [dealStore, syncStore]
  );
  const mutate = useCallback(
    (args: { id: string; name?: string }) => {
      void mutateAsync(args);
    },
    [mutateAsync]
  );

  return useMemo(
    () => ({ mutateAsync, mutate, isPending: false }),
    [mutateAsync, mutate]
  );
}
